/** @jsxImportSource @emotion/react */
import { cx } from '@emotion/css';
import type { ReactElement, ReactNode, RefObject } from 'react';
import { forwardRef } from 'react';
import type { ClassificationColors, ColorPalette, ThemeColors } from '../../utils/color-utils';
import {
  firstReadable,
  getClassificationColor,
  getColorPalette,
  getThemeColor,
  readableTextColor,
  setAlpha,
  themeColors,
} from '../../utils/color-utils';
import type { IconName } from '../Icon';
import Icon from '../Icon';

type Props = React.ComponentProps<'span'> & {
  className?: string;
  children?: ReactNode;
  testId?: string;
  /** Change the size of the badge */
  size?: 'xs' | 'sm' | 'md' | 'lg';
  /** what color do you want the badge, pass in the name of the color */
  color?: ThemeColors | ColorPalette | ClassificationColors | string;
  /** Truncate the badges text so it doesn't extend forever */
  truncate?: boolean;
  /** Use when displaying the count of something */
  pill?: boolean;
  /** Allow the text to wrap in a badge */
  wrap?: boolean;
  /** Display the badges text as all caps */
  allCaps?: boolean;
  /** Makes the numbers monospaced */
  tabularNums?: boolean;
  /** Make the badges background transparent, used for list on cards */
  transparent?: boolean;
  /** Give the badge an outline */
  outline?: boolean;
  /** Display as a block vs inline, taking up the full width */
  block?: boolean;
  /** Pass the name of an icon to display in the badge */
  icon?: IconName;
  /** Change the font-weight of the badge */
  fontWeight?: 'regular' | 'medium' | 'bold';
  /** If used on a dark color so text is readable for outline and transparent badges */
  onDark?: boolean;
  /** Display the icon as a circle, for use with just an icon */
  circle?: boolean;
  /** Change badge text alignment */
  alignText?: 'start' | 'center' | 'end';
  /** High contrast text (black or white) */
  highContrast?: boolean;
};

const ODSBadge = forwardRef(
  (
    {
      className,
      children,
      testId,
      color = 'border',
      size = 'sm',
      truncate,
      pill,
      wrap,
      allCaps,
      tabularNums = true,
      transparent,
      outline,
      block,
      icon,
      fontWeight = 'medium',
      onDark,
      circle,
      alignText = 'center',
      highContrast,
      ...componentProps
    }: Props,
    ref
  ): ReactElement => {
    const badgeColor =
      getThemeColor(color) ?? getClassificationColor(color) ?? getColorPalette(color) ?? color;
    const bgColor = !transparent || outline ? badgeColor : setAlpha(badgeColor, 0.08);
    const baseTextColors = [themeColors.white, themeColors.black];
    const textColors = highContrast ? baseTextColors : [themeColors.secondary, ...baseTextColors];
    let textColor =
      !transparent && !outline ? firstReadable(badgeColor, textColors, 4.5).toString() : badgeColor;

    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
    if (outline || transparent) {
      textColor = readableTextColor(
        textColor,
        onDark ? themeColors.black : themeColors.white
      ).toString();
    }

    let rounded = 'rounded-2';
    let fontSize = 14;
    let lineHeight = 1.25;
    let padding = '0.25em 0.6em';

    switch (size) {
      case 'xs':
        rounded = 'rounded-1';
        fontSize = allCaps ? 9 : 10;
        lineHeight = 1.2;
        break;
      case 'sm':
        rounded = 'rounded-2';
        fontSize = 12;
        break;
      case 'lg':
        rounded = 'rounded-3';
        fontSize = 16;
        break;
      default:
    }

    if (pill) {
      rounded = 'rounded-pill';
    }

    if (circle) {
      rounded = 'rounded-circle';
      fontSize -= 4;
      padding = '0.25em 0';
    }

    return (
      <span
        data-testid={testId}
        className={cx(
          'ods-badge user-select-none align-items-center gap-1 text-center',
          className,
          rounded,
          `fw-${fontWeight}`,
          {
            'text-truncate': truncate,
            'text-nowrap': !wrap,
            'text-break': wrap && !truncate,
            'tabular-nums': tabularNums,
            'text-uppercase': allCaps,
            'lh-1': circle,
          },
          block ? 'd-flex' : 'd-inline-flex',
          className
        )}
        css={(theme) => ({
          verticalAlign: 'text-bottom',
          padding,
          ...(!outline && { backgroundColor: `${bgColor} !important` }),
          ...(outline && {
            border: `1px solid ${bgColor}`,
            padding: 'calc(0.25em - 1px) calc(0.6em - 1px)',
          }),
          ...(outline && transparent && { opacity: 0.7 }),
          color: `${textColor} !important`,
          lineHeight,
          fontSize: theme.sizing.pixels(fontSize),
          '.disabled &, :disabled &': {
            opacity: 0.3,
            pointerEvents: 'none',
          },
          ...(circle && {
            width: '2em',
            height: '2em',
            textAlign: 'center',
            justifyContent: 'center',
          }),
        })}
        {...(ref && { ref: ref as RefObject<HTMLSpanElement | null> })}
        {...componentProps}
      >
        {!!icon && <Icon name={icon} />}
        {children !== undefined && (
          <span
            className={cx('w-100', {
              'text-truncate': truncate,
              'text-nowrap': !wrap,
              'text-break': wrap && !truncate,
              'text-start': alignText === 'start',
              'text-center': alignText === 'center',
              'text-end': alignText === 'end',
            })}
          >
            {children}
          </span>
        )}
      </span>
    );
  }
);

export default ODSBadge;
